import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator } from 'react-native'
import Typography from 'APP/Converse/Typography'

import I18n from 'APP/I18n'
import Button from 'APP/Converse/Button'
import FileUploadInput from 'APP/Converse/Inputs/FileUploadInput'
import { useFilePicker } from 'APP/Hooks/FilePicker.web'
import { useUploadManager } from 'APP/Hooks/useUploadManager.web'
import {
  useChannelUploadState,
  useIntakePhotoLogic,
  createIntakePhotoSubmitHandler,
} from './useIntakePhotoLogic'
import { handleIntakeFileSelection } from './fileSelection'
import {
  Container,
  FileInputContainer,
  UploadingPill,
  LayoutWrapper,
  WebButtonWrapper,
  WebRootView,
} from './style'
import { SupportedFileWithMetadata } from 'APP/Converse/FilePreview/types'
import { useDispatch } from 'APP/Hooks/redux'
import { FileUploadStatusEnum } from 'APP/Store/History/types'
import { useTheme } from 'styled-components/native'
import { type QueueFileInput } from 'APP/Lib/FileUploadHelpers'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import type { IntakePhotoBaseProps } from './types'

interface IntakePhotoAnswerPayload {
  fileName: string
  fileType: string
  file: File
}

interface IntakePhotoProps extends IntakePhotoBaseProps {
  sendAnswer: (answer?: IntakePhotoAnswerPayload | string[]) => void
}

const IntakePhoto: React.FC<IntakePhotoProps> = ({
  sendAnswer,
  disabled,
  maximum_selection = 1,
  style,
}) => {
  const inputRefMulti = useRef<HTMLInputElement>(null)
  const replacingFileIndexRef = useRef<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [duplicateFileError, setDuplicateFileError] = useState<string | null>(null)
  const duplicateErrorMessage = I18n.t('ImagePicker.errorAlert.duplicate')

  const dispatch = useDispatch()
  const theme = useTheme()
  const { channelId, channelUploadStatus, channelUpload } = useChannelUploadState()

  const { openFilePicker, onFileChange, isLoading, fileSelected, isTooLarge, setFileSelected } =
    useFilePicker({
      submitCallback: () => {},
      fileInputRef: inputRefMulti,
      resize: true,
      showMultipleFile: true,
    })

  const { files, hasPendingUpload, queueFile, removeFile, replaceFile, resetUploads } =
    useUploadManager(channelId, maximum_selection, channelUpload)

  const {
    showUploadError,
    setShowUploadError,
    setIsValidating,
    maxUploadFiles,
    isProcessingUpload,
    hasAnyFile,
    showUploadingPill,
    errorMessage,
  } = useIntakePhotoLogic({
    maximum_selection,
    files,
    hasPendingUpload,
    channelUpload,
    channelUploadStatus,
    isTooLarge,
    duplicateFileError,
  })

  useEffect(() => {
    if (!fileSelected || isTooLarge || !channelId) return

    const replacingIndex = replacingFileIndexRef.current

    replacingFileIndexRef.current = null

    handleIntakeFileSelection({
      files,
      incomingFile: fileSelected,
      comparableIncomingFile: fileSelected as QueueFileInput,
      replacingIndex: typeof replacingIndex === 'number' ? replacingIndex : null,
      queueFile,
      replaceFile,
      removeFile,
      duplicateErrorMessage,
      setDuplicateFileError,
      clearSelectedFile: () => setFileSelected(null),
    })
  }, [
    fileSelected,
    isTooLarge,
    channelId,
    files,
    queueFile,
    replaceFile,
    removeFile,
    setFileSelected,
    duplicateErrorMessage,
  ])

  useEffect(() => {
    return () => {
      resetUploads()
      setIsSubmitting(false)
      setIsValidating(false)
    }
  }, [resetUploads, setIsValidating])

  const onSubmit = createIntakePhotoSubmitHandler(files, sendAnswer, setIsValidating)

  const handleRemoveFile = useCallback(
    (file: SupportedFileWithMetadata, index: number) => {
      if (isSubmitting) {
        return
      }

      const willHaveNoFiles = files.length <= 1
      removeFile(file, index)
      setFileSelected(null)
      setDuplicateFileError(null)

      if (showUploadError && willHaveNoFiles) {
        setShowUploadError(false)
      }
    },
    [isSubmitting, files.length, removeFile, setFileSelected, showUploadError, setShowUploadError]
  )

  const handleOpenFilePicker = useCallback(
    (fileIndexToReplace?: number | React.MouseEvent<HTMLElement>) => {
      if (isSubmitting) return

      const replacingIndex = typeof fileIndexToReplace === 'number' ? fileIndexToReplace : null

      if (replacingIndex === null && files.length >= maxUploadFiles) return

      const canQueueWhileUploading = maxUploadFiles > 1
      const isUploading =
        hasPendingUpload || channelUpload?.status === FileUploadStatusEnum.UPLOADING
      const isRemoving = channelUpload?.status === FileUploadStatusEnum.REMOVING_FILE

      if (isRemoving || (!canQueueWhileUploading && isUploading)) return

      if (showUploadError && channelId) {
        setShowUploadError(false)
        dispatch(PatientHistoryActions.fileUploadClear(channelId))
      }

      if (duplicateFileError) setDuplicateFileError(null)
      replacingFileIndexRef.current = replacingIndex
      openFilePicker()
    },
    [
      files,
      hasPendingUpload,
      channelUpload?.status,
      isSubmitting,
      showUploadError,
      channelId,
      duplicateFileError,
      openFilePicker,
      maxUploadFiles,
      setShowUploadError,
      dispatch,
      setDuplicateFileError,
    ]
  )
  const isSubmitDisabled =
    !hasAnyFile || isProcessingUpload || isLoading || isSubmitting || showUploadError || disabled

  return (
    <WebRootView style={style}>
      <LayoutWrapper $isWeb testID="INTAKE_PHOTO_WEB_LAYOUT">
        <Container>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
            ref={inputRefMulti}
            style={{ display: 'none' }}
            onChange={onFileChange}
          />

          <FileInputContainer>
            <FileUploadInput
              errorMessage={errorMessage}
              onPress={isSubmitting ? undefined : handleOpenFilePicker}
              files={files}
              maxFiles={maxUploadFiles}
              onRemove={isSubmitting ? undefined : handleRemoveFile}
              isLoading={isLoading || hasPendingUpload}
              allowMultiple
            />
          </FileInputContainer>
        </Container>
        <WebButtonWrapper testID="INTAKE_PHOTO_WEB_BUTTON_WRAPPER">
          {showUploadingPill ? (
            <UploadingPill fullWidth>
              <ActivityIndicator color={theme.colors.buttonPrimaryText} />
              <Typography variant="buttonText" color="buttonPrimaryText">
                {I18n.t('IntakeComponent.uploading')}
              </Typography>
            </UploadingPill>
          ) : (
            <Button
              widthVariant="full"
              title={I18n.t('IntakeComponent.next')}
              onPress={onSubmit}
              disabled={isSubmitDisabled}
            />
          )}
        </WebButtonWrapper>
      </LayoutWrapper>
    </WebRootView>
  )
}

export default IntakePhoto
